<template>
  <b-modal size="xl" v-model="showReportDetail" id="reportDetailModal" :title="reportTitle" 
    :ok-title="$t('reports.reportDetailModal.printReport')">
    <span class="" v-if="formatDate(reportPeriod.from) !== 'Invalid Date'">
      {{ formatDate(reportPeriod.from) }} - {{ formatDate(reportPeriod.to) }}
    </span>
    <div class="spacer m" />
    <div v-if="loading">
      <LoadingAnimation />
    </div>

    <div v-if="reportData.histories && reportData.histories.length > 0">
      <b-form-group id="checkboxes" v-slot="{ ariaDescribedby }">
        <b-form-checkbox-group id="checkbox-group-2" :aria-describedby="ariaDescribedby" name="flavour-2"
          v-model="selectedFields" style="display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 20px;">
          <CustomCheckbox v-for="(field, index) in fields" :key="index" :value="selectedFields.includes(field.label)"
            :label="field.label" @input="(value) => {
              if (value) {
                selectedFields.push(field.label);
              } else {
                selectedFields = selectedFields.filter((item) => item !== field.label);
              }
            }" />

        </b-form-checkbox-group>
      </b-form-group>
      <b-table :sort-by.sync="sortBy" @sort-changed="sortingChanged" ref="emptySoonWineList"
        :items="reportData.histories" :fields="fields.filter(field => this.selectedFields.includes(field.label))"
        sticky-header class="reportDetailsTable">

        <!-- A custom formatted column -->
        <template #cell(venueWine.createdAt)="data">
          {{ formatDate(data.value, true) }}
        </template>
        <template #cell(descriptionTitle)="data">
          {{ data.value.text }}
        </template>

        <template #cell(region)="data">
          {{ printRegionHistoryText(data.value) }}
        </template>
        <template #cell(wine.region)="data">
          {{  printRegionText(data.value) }}
        </template>

        <template #cell(createdAt)="data">
          {{ formatDate(data.value, true) }}
        </template>

        <template #cell(changedByUser.username)="data">
          <div v-if="data.value">
            {{ data.value }}
          </div>
          <div v-else>
            {{ $t('general.deletedEmployee') }}
          </div>
        </template>

        <template #cell(purchasePriceAvgGenerated)="data">
          <div class="cellWithMinWidth">
            {{ $t('general.netPrice') }}: {{ data.value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + getCurrency }} <br />
            {{ $t('general.grossPrice') }}: {{ getGrossPrice(data.value) + ' ' + getCurrency }}
          </div>
        </template>

        <template #cell(addedPurchasePriceAvgGenerated)="data">
          <div class="cellWithMinWidth">
            {{ $t('general.netPrice') }}: {{ data.value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + getCurrency }} <br />
            {{ $t('general.grossPrice') }}: {{ getGrossPrice(data.value) + ' ' + getCurrency }}
          </div>
        </template>

        <template #cell(discount)="data">
          <span v-if="data.value === true">{{ $t('general.buttons.yes') }}</span>
          <span v-if="data.value !== true">{{ $t('general.buttons.no') }}</span>
        </template>

        <template #cell(revenueAll)="data">
          <div class="cellWithMinWidth">
            {{ $t('general.netPrice') }}: {{ getNetPrice(data.value) + " " + getCurrency }} <br />
            {{ $t('general.grossPrice') }}: {{ data.value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + getCurrency }}
          </div>
        </template>

        <template #cell(addedRevenueAll)="data">
          <div class="cellWithMinWidth">
            {{ $t('general.netPrice') }}: {{ getNetPrice(data.value) + " " + getCurrency }} <br />
            {{ $t('general.grossPrice') }}: {{ data.value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + getCurrency }}
          </div>
        </template>

        <template #cell(profitGenerated)="data">
          <div class="cellWithMinWidth">
            {{ $t('general.netPrice') }}: {{ getNetPrice(data.value) + " " + getCurrency }} <br />
            {{ $t('general.grossPrice') }}: {{ data.value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + getCurrency }}
          </div>
        </template>

        <template #cell(addedProfitGenerated)="data">
          <div class="cellWithMinWidth">
            {{ $t('general.netPrice') }}: {{ getNetPrice(data.value) + " " + getCurrency }} <br />
            {{ $t('general.grossPrice') }}: {{ data.value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })+ ' ' + getCurrency }}
          </div>
        </template>

        <template #cell(addedProfit)="data">
          {{ data.value + " " + getCurrency }} {{ $t('general.grossPrice') }}: {{ data.value + ' ' + getCurrency }}
        </template>
        <template #cell(causeOfReduction)="data">
          <span v-if="data.value">{{ $t('detailPanel.reduceModal.causes.' + data.value) }}</span>
          <span v-else>{{ $t('general.notDefined' + data.value) }}</span>
        </template>
        <template slot="bottom-row">
          <td v-if="bottomRowFields.some((field) => selectedFields.includes(field))" style="text-align: right"
            :colspan="lastRowColspan()">{{ this.$t('general.total') }}: </td>
          <td v-if="selectedFields.includes('Anzahl')">{{ lastRowTotalAmount() }}</td>
          <td v-if="selectedFields.includes('EK')">
            <p>{{ parseFloat(lastRowTotalPurchasePrice()).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + getCurrency }}</p>
            <p>{{ getGrossPrice(lastRowTotalPurchasePrice()) + " " + getCurrency }}</p>
          </td>
          <td v-if="selectedFields.includes('VK')">

            <p>{{ getNetPrice(lastRowTotalSellingPrice()) + " " + getCurrency }}</p>
            <p>{{ parseFloat(lastRowTotalSellingPrice()).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + getCurrency }}</p>
          </td>
          <td v-if="selectedFields.includes('Gewinn')">
            <p>{{ getNetPrice(lastRowProfit()) + " " + getCurrency }}</p>
            <p>{{ parseFloat(lastRowProfit()).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + getCurrency }}</p>
          </td>
        </template>

      </b-table>
    </div>
    <div v-else-if="!loading">
      <p>{{ this.$t('general.noData') }}</p>
    </div>
    <template #modal-footer>
        <div></div>
        <div style="display: flex; gap: 20px;">
            <!-- Emulate built in modal footer ok and cancel button actions -->
          <b-button size="sm" @click="exportCSV()">
                  {{$t('general.buttons.exportCSV')}}
          </b-button>
          <b-button size="sm" @click="printReport()" >
                  {{$t('reports.reportDetailModal.printReport')}}
          </b-button>
        </div>
       
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import DateTimeService from '@/services/DateTimeService'
import { getVenueCurrency, getNetPrice, getGrossPrice, isFloat } from '@/utils/functions'
import LoadingAnimation from '@/components/LoadingAnimation.vue'
import DataService from '@/services/StrapiService'
import CustomCheckbox from '../buttons/CustomCheckbox.vue'
import Tracking from '@/utils/tracking'

export default {
  name: 'reportDetailModal',
  data: function () {
    return {
      loading: true,
      showReportDetail: false,
      reportData: {},
      reportType: "",
      reportPeriod: {},
      totalEKValue: 0.0,
      totalVKValue: 0.0,
      reportTitle: '',
      sortBy: 'createdAt',
      bottomRowFields: ['Anzahl', 'EK', 'VK', 'Gewinn'],
      selectedFields: [],
      fieldsForTable: [],
      fieldsStandard: [

        {
          key: 'createdAt',
          label: this.$t('general.date'),
          sortable: true
        },
        {
          key: 'descriptionTitle',
          label: this.$t('general.wine'),
          sortable: true
        },
        {
          key: 'region',
          label: this.$t('general.region'),
          sortable: true
        },
        {
          key: 'year',
          label: this.$t('general.year'),
          sortable: true
        },
        {
          key: 'changedByUser.username',
          label: this.$t('general.employee'),
          sortable: true
        },
        {
          key: 'discount',
          label: this.$t('detailPanel.discountModal.discountPercentage'),
          sortable: false
        },
        {
          key: 'soldAmountFloat',
          label: this.$t('general.amount'),
          sortable: true
        },
        {
          key: 'purchasePriceAvgGenerated',
          label: this.$t('general.ek_short'),
          sortable: true
        },
        {
          key: 'revenueAll',
          label: this.$t('general.vk_short'),
          sortable: true
        },
        {
          key: 'profitGenerated',
          label: this.$t('general.profit'),
          sortable: true
        }
      ],
      fieldsMoved: [
        {
          key: 'venueWine.createdAt',
          label: this.$t('reports.reportDetailModal.addedOn'),
          sortable: true
        },
        {
          key: 'createdAt',
          label: this.$t('reports.reportDetailModal.movedOn'),
          sortable: true
        },
        {
          key: 'newVenue.title',
          label: this.$t('general.location'),
          sortable: true
        },
        {
          key: 'descriptionTitle',
          label: this.$t('general.wine'),
          sortable: true
        },
        {
          key: 'region',
          label: this.$t('general.region'),
          sortable: true
        },
        {
          key: 'year',
          label: this.$t('general.year'),
          sortable: true
        },
        {
          key: 'changedByUser.username',
          label: this.$t('general.employee'),
          sortable: true
        },
        {
          key: 'soldAmountFloat',
          label: this.$t('general.amount'),
          sortable: true
        },
        {
          key: 'purchasePriceAvgGenerated',
          label: this.$t('general.ek_short'),
          sortable: true
        },
        {
          key: 'revenueAll',
          label: this.$t('general.vk_short'),
          sortable: true
        },
        {
          key: 'profitGenerated',
          label: this.$t('general.profit'),
          sortable: true
        }
      ],
      fieldsAdded: [

        {
          key: 'createdAt',
          label: this.$t('general.date'),
          sortable: true
        },
        {
          key: 'descriptionTitle',
          label: this.$t('general.wine'),
          sortable: true
        },
        {
          key: 'region',
          label: this.$t('general.region'),
          sortable: true
        },
        {
          key: 'year',
          label: this.$t('general.year'),
          sortable: true
        },
        {
          key: 'changedByUser.username',
          label: this.$t('general.employee'),
          sortable: true
        },
        {
          key: 'addedBottleAmountFloat',
          label: this.$t('general.amount'),
          sortable: true
        },
        {
          key: 'addedPurchasePriceAvgGenerated',
          label: this.$t('general.ek_short'),
          sortable: true
        },
        {
          key: 'addedRevenueAll',
          label: this.$t('general.vk_short'),
          sortable: true
        },
        {
          key: 'addedProfitGenerated',
          label: this.$t('general.profit'),
          sortable: true
        }
      ],
      fieldsDeleted: [
        {
          key: 'createdAt',
          label: this.$t('reports.reportDetailModal.movedOn'),
          sortable: true
        },
        {
          key: 'descriptionTitle',
          label: this.$t('general.wine'),
          sortable: true
        },
        {
          key: 'region',
          label: this.$t('general.region'),
          sortable: true
        },
        {
          key: 'year',
          label: this.$t('general.year'),
          sortable: true
        },
        {
          key: 'causeOfReduction',
          label: this.$t('reports.reportDetailModal.causeOfReduction'),
          sortable: false
        },
        {
          key: 'soldAmountFloat',
          label: this.$t('general.amount'),
          sortable: true
        },
        {
          key: 'purchasePriceAvgGenerated',
          label: this.$t('general.ek_short'),
          sortable: true
        },
        {
          key: 'revenueAll',
          label: this.$t('general.vk_short'),
          sortable: true
        },
        {
          key: 'profitGenerated',
          label: this.$t('general.profit'),
          sortable: true
        }
      ],
      fieldsStockAtDate: [
         {
          key: 'wine.winery.title',
          label: this.$t('general.winery'),
          sortable: true
        },
        {
          key: 'title',
          label: this.$t('general.wine'),
          sortable: true
        },
        { 
          key: 'wine.grapeVariety.title',
          label: this.$t('general.grapeVariety'),
          sortable: true
        },
        {
          key: 'wine.wineType.localeKey',
          label: this.$t('general.winetype'),
          sortable: true,
          formatter: (value) => this.$t('wineTypes.' + value)
        },
        {
          key: 'wine.land.title',
          label: this.$t('general.country'),
          sortable: true,
        },
        {
          key: 'wine.region',
          label: this.$t('general.region'),
          sortable: true
        },
        {
          key: 'year',
          label: this.$t('general.year'),
          sortable: true
        },
        {
          key: 'bottleSize.amount',
          label: this.$t('general.bottleSize'),
          sortable: true
        },
        {
          key: 'amountFloat',
          label: this.$t('general.amount'),
          sortable: true
        },
        {
          key: 'purchasePriceAvg',
          label: this.$t('general.ek_short'),
          sortable: true
        },
          {
            key: 'sellingPrice',
            label: this.$t('general.vk_short'),
            sortable: true
          }
      ]
    }
  },
  created() {
    this.selectedFields = this.fields.map(field => field.label)
  },
  methods: {
    getNetPrice,
    getGrossPrice,
    async showModal(report) {
      this.loading = true
      this.reportData = {}
      this.reportType = ""
      this.showReportDetail = true
      this.reportPeriod = {
        from: report.attributes.dateFrom,
        to: report.attributes.dateTo
      }
      this.reportTitle = report.attributes.title

      Tracking.trackEvent(
        Tracking.CATEGORY_REPORT,
        Tracking.ACTION_OPEN_REPORT_DETAIL,
        report.attributes.title,
        report.id
      );

      if (report.attributes.type.includes('stock_at_date')) {
        let date_string = report.attributes.dateFrom
        await DataService.selectVenueWinesAtDate(null, date_string, true).then((response) => {
          this.reportData = response['data']
          this.reportType = 'stock_at_date'
          this.loading = false
          this.selectedFields = this.fields.map(field => field.label)
        }).catch((err) => {
          console.log(err)
          this.loading = false
        })
      } else {
        await DataService.findVenueWineHistoryReports(report.attributes.filterQuery).then((response) => {
          this.reportData = response['data'].data
          this.reportType = report.attributes.type
          this.loading = false
          console.log(this.reportData)
        }).catch((err) => {
          console.log(err)
          this.loading = false
        })
      }
    },
    formatDate(date, time) {
      var newDate = new Date(date)
      var parsedDatetime = DateTimeService.dateToFormattedstring(newDate, time, this.$i18n.locale)
      return parsedDatetime
    },
    printReport() {
        // Hide the checkboxes before printing
        document.getElementById('checkboxes').style.display = 'none';
        window.print();
        // Show the checkboxes again after printing
        document.getElementById('checkboxes').style.display = 'flex';

        Tracking.trackEvent(
          Tracking.CATEGORY_REPORT,
          Tracking.ACTION_PRINT_REPORT,
          this.reportTitle
        );
    },
    printRegionHistoryText(data) {
      return data.regionLocaleKey !== null && data.regionLocaleKey !== undefined ? this.$t('regions.' + data.regionLocaleKey) : data.regionTitle 
    },
    printRegionText(region) {
      return region && region.localeKey && region.localeKey !== undefined ? this.$t('regions.' + region.localeKey) :  region.title
    },

    exportCSV() {
     //create reportsdata object based on the selected fields
      var selectedFields = this.fields.filter(field => this.selectedFields.includes(field.label))
      var reportData = this.reportData.histories.map(item => {
        var newItem = {}
        selectedFields.forEach(field => {
          if (field.key === 'descriptionTitle') {
            newItem[field.label] = item.descriptionTitle.text
          } else if (field.key === 'region') {
            newItem[field.label] =   this.printRegionHistoryText(item[field.key]) 
          } else if (field.key === 'wine.region') {
            newItem[field.label] =  this.printRegionText(item.wine.region)
          } else if (field.key === 'wine.land.title') {
             newItem[field.label] = item.wine.land.title
          } else if (field.key === 'wine.winery.title') {
            newItem[field.label] = item.wine.winery.title
          } else if (field.key === 'wine.grapeVariety.title') {
            newItem[field.label] = item.wine.grapeVariety.title
          } else if (field.key === 'wine.wineType.localeKey') {
            newItem[field.label] = this.$t('wineTypes.' + item.wine.wineType.localeKey)
          } else if (field.key === 'changedByUser.username') {
            newItem[field.label] = item['changedByUser']['username'] ? item['changedByUser']['username'] : this.$t('general.deletedEmployee')
          } else if (field.key === 'venueWine.createdAt') {
            newItem[field.label] = this.formatDate(item.venueWine.createdAt, true)
          } else if (field.key === 'createdAt') {
            newItem[field.label] = this.formatDate(item.createdAt, true)
          } else if (field.key === 'purchasePriceAvgGenerated' || field.key === 'addedPurchasePriceAvgGenerated') {
            newItem[field.label + ' ' + this.$t('general.netPrice')] = item[field.key].toFixed(2) + ' ' + this.getCurrency
          } else if (field.key === 'revenueAll' || field.key === 'addedRevenueAll') {
            newItem[field.label + ' ' + this.$t('general.grossPrice')] = item[field.key].toFixed(2) + ' ' + this.getCurrency
          } else if (field.key === 'profitGenerated' || field.key === 'addedProfitGenerated') {
            newItem[field.label + ' ' + this.$t('general.grossPrice')] = item[field.key].toFixed(2) + ' ' + this.getCurrency
          } else if (field.key === 'addedBottleAmountFloat' || field.key === 'soldAmountFloat') {
            newItem[field.label] = item[field.key].toFixed(2) + ' ' + this.getCurrency
          } else if (field.key === 'discount') {
            newItem[field.label] = item[field.key] ? this.$t('general.buttons.yes') : this.$t('general.buttons.no')
          } else if (field.key === 'causeOfReduction') {
            newItem[field.label] = item[field.key] ? this.$t('detailPanel.reduceModal.causes.' + item[field.key]) : this.$t('general.notDefined')
          }

          else {
            if (field.key.includes('.')) {
              var keys = field.key.split('.')
              newItem[field.label] = item[keys[0]][keys[1]]
            } else {
              newItem[field.label] = item[field.key]
            }
          }

         
        })
        return newItem
      })
      
      var csv = this.$papa.unparse(reportData);
      var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      var link = document.createElement("a");
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      var dateForFile = this.formatDate(this.reportPeriod.from)
      dateForFile = this.replaceAndCutString(dateForFile)
      
      var reportName = this.replaceAndCutString(this.reportTitle)
      var venueTitle = this.getVenue.attributes.title
      venueTitle = this.replaceAndCutString(venueTitle)

      
      var filename = "poolinq_report_" + venueTitle + "_" + reportName + "_" + dateForFile + ".csv";
      link.setAttribute("download",  filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Tracking.trackEvent(
        Tracking.CATEGORY_REPORT,
        Tracking.ACTION_EXPORT_CSV,
        this.reportTitle
      );
    },

    replaceAndCutString(string) {
      string = string.replace(/ /g, "_")
      string = string.replace(/,/g, "_")
      string = string.replace(/-/g, "_")
      string = string.replace(/:/g, "_")
      string = string.replace(/_/g, "_")
      string = string.replace(/\./g, "_")
      // cut the reportName to 20 characters
      if (string.length > 20) {
        string = string.substring(0, 20)
      }
      return string
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.refreshTable()
    },
    refreshTable() {
      this.$root.$emit('bv::table::refresh', 'organizations-table')
    },
    lastRowColspan() {
      var totalcols = this.fields.filter(field => this.selectedFields.includes(field.label)).length
      var bottomRowFieldsInSelectedFields = this.bottomRowFields.filter(field => this.selectedFields.includes(field))
      return totalcols - 4 > 0 ? totalcols - bottomRowFieldsInSelectedFields.length : 1
    },
    lastRowTotalAmount() {
      var totalAmount = 0
      if (this.reportType === 'added') {
        totalAmount = this.reportData.totalAddedBottleAmountFloat
      } else {
        totalAmount = this.reportData.totalSoldBottleAmountFloat
      }
      if (isFloat(totalAmount)) {
        totalAmount = totalAmount.toFixed(2)
      }
      return totalAmount
    },
    lastRowTotalPurchasePrice() {
      if (this.reportType === 'added') {
        if (this.reportData.totalAddedPurchasePriceAvgGenerated) {
          return this.reportData.totalAddedPurchasePriceAvgGenerated.toFixed(2)
        }
      } else {
        if (this.reportData.totalPurchasePriceAvgGenerated) {
          return this.reportData.totalPurchasePriceAvgGenerated.toFixed(2)
        }
      }
    },
    lastRowTotalSellingPrice() {
      if (this.reportType === 'added') {
        if (this.reportData.totalAddedRevenueBottle) {
          return this.reportData.totalAddedRevenueBottle.toFixed(2)
        }
      } else {
        if (this.reportData.totalRevenueBottle) {
          return this.reportData.totalRevenueBottle.toFixed(2)
        }
      }
    },
    lastRowProfit() {
      if (this.reportType === 'added') {
        if (this.reportData.totalAddedProfitGenerated) {
          return this.reportData.totalAddedProfitGenerated.toFixed(2)
        }
      } else {
        if (this.reportData.totalProfitGenerated) {
          return this.reportData.totalProfitGenerated.toFixed(2)
        }
      }
    },
    lastRowCombinedStockAtDate() {
      if (this.reportType === 'stock_at_date') {
        if (this.reportData.totalCombinedStockAtDate) {
          return this.reportData.totalCombinedStockAtDate.toFixed(2)
        }
      } 
    }
  },
  computed: {
    fields() {
      if (this.reportType === 'moved') {
        return this.fieldsMoved
      } else if (this.reportType === 'added') {
        return this.fieldsAdded
      } else if (this.reportType === 'deleted') {
        return this.fieldsDeleted
      } else  if (this.reportType === 'stock_at_date') {
        return this.fieldsStockAtDate
      } else {
        return this.fieldsStandard
      }
    },
    getCurrency() {
      return getVenueCurrency(this.getVenue)
    },
    ...mapGetters(['getVenue'])
  },
  components: { LoadingAnimation, CustomCheckbox }
}
</script>
